import { useEffect, useRef, useState } from 'react';
import { useUserInfo } from '../../../../helpers/hooks/useUserInfo';
import { PostViewOptions } from '../../constants/constants';
import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { getNodesFromConnection } from '../../../../helpers/graphql';
import { IGQLPostProductUnitNode } from '../../../../interfaces/post';
import { getCatchGearItems } from '../../helpers/getCatchGearItems';
import { Post } from '../../types/Post';
import { PostDetailsView } from '../PostDetailsView/PostDetailsView';
import { PostLikesView } from '../PostLikesView/PostLikesView';
import { PostMediaSection } from '../PostMediaSection/PostMediaSection';
import { PostCommentsView } from './PostCommentsView/PostCommentsView';
import { RegisterWallView } from '../RegisterWallView/RegisterWallView';
interface PostsViewerDesktopProps {
  posts: Post[];
  currentPostId: string;
  showDisplayEntity?: boolean;
}

/**
 * Component for displaying posts in a desktop view.
 * This is mainly used for displaying a list of posts in a modal view.
 * It can also be used to display a single post in a full screen view without a modal.
 *
 * @component
 * @param {PostsViewerDesktopProps} props - The properties for the component.
 * @param {Post[]} props.posts - The list of posts to display.
 * @param {string} props.currentPostId - The ID of the currently selected post.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * This component handles different views such as comments, details, likes, and a register wall.
 * It also manages the state of the selected view and handles user interactions to switch between views.
 */
export const PostsViewerDesktop = ({ posts, currentPostId }: PostsViewerDesktopProps) => {
  const { currentPage } = useAnalytics();

  const [selectedView, setSelectedView] = useState<PostViewOptions>('postComments');
  const desiredViewName = useRef<PostViewOptions>('postComments');
  const { isLoggedIn } = useUserInfo();
  const currentPost = posts.find(post => post._id === currentPostId);
  const hasCatch = !!currentPost?.catch;
  const usedGearsCount = currentPost?.displayProductUnits.totalCount;
  const images = getNodesFromConnection(currentPost?.images);
  const video = currentPost?.video;
  const usedGearFromPost = getNodesFromConnection<IGQLPostProductUnitNode>(
    currentPost?.productUnits,
  );
  const user = currentPost?.user;
  const userNickname = user?.nickname ?? '';
  const userAvatarUrl = user?.avatar?.url ?? '';

  const displayEntity = currentPost?.displayEntity;
  const showDisplayEntity = displayEntity?.displayName && displayEntity.displayIcon;
  const displayUserNickname =
    showDisplayEntity && displayEntity.displayName ? displayEntity.displayName : userNickname;
  const displayUserAvatarUrl =
    showDisplayEntity && displayEntity.displayIcon.url
      ? displayEntity.displayIcon.url
      : userAvatarUrl;

  const hasImages = images.length > 0;
  const hasVideo = video?.screenshot;
  const isPostWithoutImagesAndVideo = !hasCatch && !hasImages && !hasVideo;
  const scrollSelector = selectedView === 'postDetailsWithScroll' ? '#post-products' : undefined;
  const isPostCommentsView = selectedView === 'postComments';
  const isPostDetailsView = selectedView.includes('postDetails');
  const isPostLikesView = selectedView === 'postLikes';
  const isRegisterWallView = selectedView === 'registerWallView';

  useEffect(() => {
    if (isLoggedIn) {
      setSelectedView(desiredViewName.current);
    }
  }, [isLoggedIn]);

  const handleBackButtonClick = (): void => {
    setSelectedView('postComments');
  };

  const handleViewChange = (viewName: PostViewOptions): void => {
    if (!isLoggedIn) {
      desiredViewName.current = viewName;
      setSelectedView('registerWallView');
      return;
    }
    setSelectedView(viewName);
  };

  const handleGearButtonClick = (): void => {
    handleViewChange('postDetailsWithScroll');
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'read-more',
        source: 'post-viewer-gear',
        page: currentPage,
      },
    });
  };

  return (
    <div
      className={`${isPostWithoutImagesAndVideo ? 'mx-auto max-h-full w-2/3' : 'size-full'} grid grid-cols-5 grid-rows-1 rounded-lg bg-white`}
      data-testid="PostsViewerDesktop"
    >
      {!isPostWithoutImagesAndVideo && (
        <div className="col-start-1 col-end-4 row-start-1">
          <PostMediaSection
            onGearButtonClick={handleGearButtonClick}
            usedGearsCount={usedGearsCount}
            userNickname={displayUserNickname}
            images={images}
            video={video ?? undefined}
            hasCatch={hasCatch}
          />
        </div>
      )}

      <div
        className={`${isPostWithoutImagesAndVideo ? 'col-start-1 col-end-6 rounded-lg' : 'col-start-4 col-end-6 rounded-r-lg'} row-start-1`}
      >
        {isPostCommentsView && (
          <PostCommentsView
            hasCatch={hasCatch}
            posts={posts}
            currentPostId={currentPostId}
            onViewChange={handleViewChange}
            usedGearFromPost={getCatchGearItems(usedGearFromPost)}
          />
        )}
        {isPostDetailsView && (
          <PostDetailsView
            hasCatch={hasCatch}
            currentPostId={currentPostId}
            onBackButtonClick={handleBackButtonClick}
            scrollSelector={scrollSelector}
          />
        )}
        {isPostLikesView && (
          <PostLikesView currentPostId={currentPostId} onBackButtonClick={handleBackButtonClick} />
        )}
        {isRegisterWallView && (
          <RegisterWallView
            onBackButtonClick={handleBackButtonClick}
            userAvatarUrl={displayUserAvatarUrl}
            userNickname={displayUserNickname}
          />
        )}
      </div>
    </div>
  );
};
